<template>
  <div class="row">
    <!-- Breadcrumb & Header -->
    <div class="col-12 mb-4">
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
        <div>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb bg-transparent p-0 mb-1">
              <li class="breadcrumb-item">
                <router-link :to="{ name: 'ListPayment' }" class="text-muted text-decoration-none">
                  {{ $t('superadmin.paymentChannelsTitle') }}
                </router-link>
              </li>
              <li class="breadcrumb-item active text-white" aria-current="page">
                {{ $t('superadmin.detailPaymentMethod') }}
              </li>
            </ol>
          </nav>
          <h2 class="text-white font-weight-bold mb-1">{{ $t('superadmin.detailPaymentMethod') }}</h2>
          <p class="text-muted small mb-0">{{ $t('superadmin.paymentChannelsSubtitle') }}</p>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button type="button" @click="back" class="btn btn-secondary mr-2">
            <i class="mdi mdi-arrow-left mr-1"></i> {{ $t('common.back') }}
          </button>
          <router-link
            :to="{ name: 'EditPayment', params: { id: id } }"
            class="btn btn-primary mr-2"
          >
            <i class="mdi mdi-pencil mr-1"></i> {{ $t('common.edit') }}
          </router-link>
          <button
            type="button"
            @click="confirmDelete"
            class="btn btn-danger"
          >
            <i class="mdi mdi-delete-outline mr-1"></i> {{ $t('common.delete') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="col-12 col-xl-10 grid-margin">
      <div v-if="loading" class="card">
        <div class="card-body text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ $t('common.loading') }}</span>
          </div>
          <p class="text-muted mt-2">{{ $t('common.loading') }}</p>
        </div>
      </div>

      <div v-else class="card">
        <div class="card-body">
          <!-- Hero Section inside card -->
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center p-3 mb-4 rounded-lg" style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px;">
            <div class="d-flex align-items-center mb-3 mb-md-0">
              <div
                class="rounded-lg mr-3 d-flex align-items-center justify-content-center"
                style="width: 52px; height: 52px; background: rgba(56, 189, 248, 0.15); border: 1px solid rgba(56, 189, 248, 0.3); border-radius: 12px; flex-shrink: 0;"
              >
                <i class="mdi mdi-credit-card-outline text-info" style="font-size: 28px;"></i>
              </div>
              <div>
                <h4 class="text-white font-weight-bold mb-1">{{ payment.name }}</h4>
                <div class="d-flex align-items-center gap-2">
                  <span class="text-muted small">{{ $t('superadmin.channelCode') }}:</span>
                  <span class="badge badge-outline-secondary font-mono">{{ payment.code || 'N/A' }}</span>
                </div>
              </div>
            </div>
            <div>
              <span
                class="badge px-3 py-2"
                :class="payment.status === 'Active' ? 'badge-success' : 'badge-secondary'"
                style="font-size: 13px;"
              >
                <i class="mdi mr-1" :class="payment.status === 'Active' ? 'mdi-check-circle' : 'mdi-close-circle'"></i>
                {{ payment.status === 'Inactive' ? $t('common.inactive') : $t('common.active') }}
              </span>
            </div>
          </div>

          <!-- Detail Grid -->
          <div class="row g-4">
            <div class="col-md-6">
              <div class="p-3 rounded-lg h-100" style="background: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 10px;">
                <h6 class="text-muted text-uppercase small font-weight-bold mb-3">{{ $t('superadmin.channelInfo') }}</h6>
                
                <div class="mb-3">
                  <span class="text-muted small d-block">{{ $currentLocale === 'id' ? 'Tipe Saluran' : 'Channel Type' }}</span>
                  <span class="text-white font-weight-medium text-capitalize">
                    {{ formatType(payment.type) }}
                  </span>
                </div>

                <div class="mb-3">
                  <span class="text-muted small d-block">{{ $t('superadmin.baseFee') }}</span>
                  <span class="text-success font-weight-bold" style="font-size: 16px;">
                    {{ formatRupiah(payment.price) }}
                  </span>
                </div>

                <div>
                  <span class="text-muted small d-block">{{ $currentLocale === 'id' ? 'Terakhir Diperbarui' : 'Last Updated' }}</span>
                  <span class="text-white small">
                    {{ formatDate(payment.updated_at) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="p-3 rounded-lg h-100" style="background: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 10px;">
                <h6 class="text-muted text-uppercase small font-weight-bold mb-3">{{ $t('billing.information') }}</h6>
                
                <div class="mb-3">
                  <span class="text-muted small d-block">{{ $t('billing.accountNumber') }} / ID Merchant</span>
                  <span class="text-white font-weight-medium font-mono">
                    {{ payment.account_number || '-' }}
                  </span>
                </div>

                <div class="mb-3">
                  <span class="text-muted small d-block">{{ $t('billing.senderName') }} / {{ $currentLocale === 'id' ? 'Nama Penerima' : 'Beneficiary Name' }}</span>
                  <span class="text-white font-weight-medium">
                    {{ payment.account_name || '-' }}
                  </span>
                </div>

                <div>
                  <span class="text-muted small d-block">{{ $t('common.status') }}</span>
                  <span class="text-muted small">
                    {{ payment.status === 'Active' ? ($currentLocale === 'id' ? 'Aktif dan tersedia bagi mitra perusahaan saat pembayaran.' : 'Active and available for enterprise clients at checkout.') : ($currentLocale === 'id' ? 'Dinonaktifkan sementara dari halaman pembayaran.' : 'Temporarily disabled from checkout.') }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Instructions -->
            <div class="col-12 mt-3">
              <div class="p-3 rounded-lg" style="background: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 10px;">
                <h6 class="text-muted text-uppercase small font-weight-bold mb-2">{{ $currentLocale === 'id' ? 'Instruksi Pembayaran untuk Mitra' : 'Checkout Instructions for Clients' }}</h6>
                <p class="text-white mb-0" style="white-space: pre-line; line-height: 1.6;">
                  {{ payment.instructions || ($currentLocale === 'id' ? 'Belum ada instruksi khusus untuk saluran pembayaran ini.' : 'No specific instructions configured for this payment channel.') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDetailPayment, deletePayment } from "@/services/paymentService";
import Swal from "sweetalert2";
import moment from "moment";

export default {
  name: "PaymentDetailView",
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      payment: {},
      loading: true,
    };
  },
  mounted() {
    this.fetchDetailPayment();
  },
  methods: {
    async fetchDetailPayment() {
      this.loading = true;
      try {
        const response = await getDetailPayment(this.id);
        this.payment = response || {};
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: this.$t('common.error'),
          text: this.$currentLocale === 'id' ? "Metode pembayaran tidak ditemukan." : "Payment method not found.",
          confirmButtonColor: "#3b82f6",
        });
        this.back();
      } finally {
        this.loading = false;
      }
    },
    formatRupiah(number) {
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
      }).format(number || 0);
    },
    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD MMMM YYYY, HH:mm");
    },
    formatType(type) {
      const isId = this.$currentLocale === 'id';
      const map = {
        digital: isId ? "Gerbang Digital (Xendit / QRIS)" : "Digital Gateway (Xendit / QRIS)",
        manual: isId ? "Transfer Bank Manual" : "Manual Bank Transfer",
        va: isId ? "Akun Virtual" : "Virtual Account",
        qris: isId ? "QRIS Dinamis" : "Dynamic QRIS",
      };
      return map[type] || type || (isId ? "Gerbang Digital" : "Digital Gateway");
    },
    back() {
      this.$router.push({ name: "ListPayment" });
    },
    confirmDelete() {
      const isId = this.$currentLocale === 'id';
      Swal.fire({
        title: isId ? "Hapus Metode Pembayaran?" : "Delete Payment Method?",
        text: isId ? `Saluran "${this.payment.name || ''}" akan dihapus secara permanen.` : `Channel "${this.payment.name || ''}" will be permanently removed.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#64748b",
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.cancel'),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await deletePayment(this.id);
            Swal.fire({
              icon: "success",
              title: this.$t('common.success'),
              text: isId ? "Saluran pembayaran berhasil dihapus." : "Payment channel has been deleted.",
              confirmButtonColor: "#3b82f6",
            });
            this.$router.push({ name: "ListPayment" });
          } catch (error) {
            Swal.fire({
              icon: "error",
              title: this.$t('common.error'),
              text: error.response?.data?.message || (isId ? "Gagal menghapus saluran pembayaran." : "Failed to delete payment channel."),
              confirmButtonColor: "#3b82f6",
            });
          }
        }
      });
    },
  },
};
</script>